import { useDraggable } from '@dnd-kit/core';
import { subscribe, EVENTS } from '@sendible/event-manager';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToastNotificationContext } from '@sendible/design-system';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import { Container } from './index.styles';
import { canDragMessage, isDragBlockedByRecurrence } from '../../../../utils';

/* Matches the PointerSensor activationConstraint on the calendar's DndContext, so the warning
   fires at the exact point a draggable card would have started moving, and never on a click. */
const DRAG_ATTEMPT_DISTANCE = 4;
const WARNING_TIMEOUT_SECONDS = 5;

interface Props {
  message: CalendarMessage;
  dayKey: string;
  children: React.ReactNode;
}

export const DraggableCard = ({ message, dayKey, children }: Props) => {
  const [isMediaPreviewOpen, setIsMediaPreviewOpen] = useState(false);
  const { showToastNotification } = useToastNotificationContext();
  const { t } = useTranslation('calendar');
  const pressOriginRef = useRef<{ x: number; y: number } | null>(null);
  const hasWarnedRef = useRef(false);
  const isDragDisabled = isMediaPreviewOpen || !canDragMessage(message);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    /* Recurring occurrences are expanded into one card per date but all share the
       underlying message id, so the send date is what makes a draggable id unique. */
    id: `msg-${message.id}-${message.sendDateLocalized}`,
    data: { message, fromDateKey: dayKey },
    disabled: isDragDisabled,
  });

  /* Recurring cards never reach dnd-kit, so the attempted drag has to be spotted here. */
  const warnsOnDragAttempt = !isMediaPreviewOpen && isDragBlockedByRecurrence(message);

  const handlePointerDown = (event: React.PointerEvent) => {
    pressOriginRef.current = { x: event.clientX, y: event.clientY };
    hasWarnedRef.current = false;
  };

  const handlePointerMove = (event: React.PointerEvent) => {
    /* A press that ended off the card leaves a stale origin behind, so confirm a button is
       still down before treating movement as a drag. Touch only emits moves while held. */
    if (event.buttons === 0) {
      pressOriginRef.current = null;

      return;
    }

    const origin = pressOriginRef.current;

    if (!origin || hasWarnedRef.current) return;
    if (Math.hypot(event.clientX - origin.x, event.clientY - origin.y) < DRAG_ATTEMPT_DISTANCE) return;

    hasWarnedRef.current = true;

    showToastNotification({
      hideTimeout: WARNING_TIMEOUT_SECONDS,
      notificationText: t('RescheduleRecurringNotAllowed'),
      type: NotificationTypeEnum.Info,
      showCloseButton: true,
    });
  };

  const handlePointerEnd = () => {
    pressOriginRef.current = null;
  };

  useEffect(() => {
    const unsubscribe = subscribe(EVENTS.MEDIA_PREVIEW_TOGGLE, (data?: { isOpen?: boolean }) => {
      setIsMediaPreviewOpen(Boolean(data?.isOpen));
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <Container
      data-dragging={isDragging || undefined}
      ref={setNodeRef}
      $isDragging={isDragging}
      {...(!isDragDisabled ? listeners : {})}
      {...(!isDragDisabled ? attributes : {})}
      {...(warnsOnDragAttempt
        ? {
            onPointerDown: handlePointerDown,
            onPointerMove: handlePointerMove,
            onPointerUp: handlePointerEnd,
            onPointerCancel: handlePointerEnd,
          }
        : {})}
    >
      {children}
    </Container>
  );
};
